import axiosDefault from "axios";
import * as axiosNamespace from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearAuthTokens,
} from "./tokenStorage";

const Axios = [
  axiosDefault,
  axiosNamespace,
  axiosNamespace.default,
  axiosDefault && axiosDefault.default,
].find((candidate) => candidate && typeof candidate.create === "function");

function getBaseURL() {
  let baseURL = "http://localhost:8000/api/";
  if (process.env.REACT_APP_ENV == "local") {
    baseURL = "http://localhost:8000/api/";
  } else if (process.env.REACT_APP_ENV == "dev") {
    baseURL = "https://dev.opentaxi.fi/api/";
  } else if (process.env.REACT_APP_ENV == "production") {
    baseURL = "https://app.opentaxi.fi/api/";
  }

  return { baseURL };
}

export let { baseURL } = getBaseURL();

function headers() {
  let headers = {
    "content-type": "application/json",
  };
  return { headers };
}

if (!Axios || typeof Axios.create !== "function") {
  throw new Error("Axios failed to load (missing create())");
}

const axios = Axios.create({
  withCredentials: false,
  ...getBaseURL(),
  ...headers(),
});

function handleReques(config) {
  return config;
}

function handleRequesErrors(error) {
  return error;
}

function handleResponse(response) {
  return response.data;
}

function normalizeClientError(error) {
  if (!error || !error.response) {
    return Promise.reject({ data: null, status: 404, statusText: "Not found" });
  }
  const { data, status, statusText } = error.response;
  return Promise.reject({ data, status, statusText });
}

/** One in-flight refresh for concurrent 401s. */
let refreshPromise = null;

function hasJwtAuthorization(config) {
  if (!config || !config.headers) return false;
  const h = config.headers;
  const auth = h.Authorization ?? h.authorization;
  return typeof auth === "string" && auth.trim().toLowerCase().startsWith("jwt ");
}

function shouldSkipAuthRefreshRetry(config) {
  if (!config) return true;
  if (config.skipAuthRefresh) return true;
  if (config.__isAuthRetry) return true;
  const url = config.url || "";
  if (url.includes("token/refresh")) return true;
  if (url === "token/" || url.endsWith("token/")) return true;
  return false;
}

function runRefresh() {
  if (refreshPromise) return refreshPromise;
  const refresh = getRefreshToken();
  if (!refresh) {
    return Promise.reject(new Error("no refresh token"));
  }
  refreshPromise = axios
    .post(
      "token/refresh/",
      { refresh },
      { skipAuthRefresh: true },
    )
    .then((body) => {
      const access = body && (body.token || body.access);
      if (body && body.refresh) setRefreshToken(body.refresh);
      if (access) setAccessToken(access);
      if (!access) throw new Error("refresh response missing access");
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

async function handleResponseErrors(error) {
  const original = error.config;

  if (!error || !error.response) {
    return Promise.reject({ data: null, status: 404, statusText: "Not found" });
  }

  if (original?.skipAuthRefresh) {
    return normalizeClientError(error);
  }

  const status = error.response.status;
  if (
    status === 401 &&
    original &&
    !shouldSkipAuthRefreshRetry(original) &&
    hasJwtAuthorization(original)
  ) {
    if (!getRefreshToken()) {
      clearAuthTokens();
      return normalizeClientError(error);
    }
    try {
      await runRefresh();
    } catch (_e) {
      clearAuthTokens();
      return normalizeClientError(error);
    }
    const next = getAccessToken();
    if (!next) {
      clearAuthTokens();
      return normalizeClientError(error);
    }
    original.__isAuthRetry = true;
    original.headers = {
      ...(original.headers || {}),
      Authorization: `JWT ${next}`,
    };
    return axios(original);
  }

  return normalizeClientError(error);
}

// Add a request interceptor
axios.interceptors = axios.interceptors || {
  request: { use: () => {} },
  response: { use: () => {} },
};
axios.interceptors.request = axios.interceptors.request || { use: () => {} };
axios.interceptors.response = axios.interceptors.response || { use: () => {} };
axios.interceptors.request.use(handleReques, handleRequesErrors);
// Add a response interceptor
axios.interceptors.response.use(handleResponse, handleResponseErrors);

axios.defaults = axios.defaults || {};
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

function http(configOrUrl, config) {
  if (typeof configOrUrl === "string") {
    return axios.request({
      url: configOrUrl,
      ...(config || {}),
    });
  }

  return axios.request(configOrUrl);
}

http.defaults = axios.defaults;
http.interceptors = axios.interceptors;
http.request = axios.request.bind(axios);
http.get = axios.get.bind(axios);
http.delete = axios.delete.bind(axios);
http.head = axios.head.bind(axios);
http.options = axios.options.bind(axios);
http.post = axios.post.bind(axios);
http.put = axios.put.bind(axios);
http.patch = axios.patch.bind(axios);

export default http;
